import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import type { RootState } from '../store';
import { getFlips, type Flip } from '../../services/flipsService';

interface FlipsState {
  flips: Flip[];
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: FlipsState = {
  flips: [],
  status: 'idle',
  error: null,
};

// Async thunk to fetch flips
export const fetchFlips = createAsyncThunk<Flip[], void, { rejectValue: string; state: RootState }>(
  'flips/fetchFlips',
  async (_, thunkAPI) => {
    try {
      return await getFlips();
    } catch (error: unknown) {
      let errorMessage = 'Failed to fetch flips';
      if (axios.isAxiosError(error)) {
        const message = error.response?.data?.message;
        if (typeof message === 'string') {
          errorMessage = message;
        }
      } else if (error instanceof Error && error.message) {
        errorMessage = error.message;
      }
      return thunkAPI.rejectWithValue(errorMessage);
    }
  },
);

const flipsSlice = createSlice({
  name: 'flips',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchFlips.pending, state => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchFlips.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.flips = action.payload;
      })
      .addCase(fetchFlips.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload ?? 'Failed to fetch flips';
      });
  },
});

// Selectors
export const selectAllFlips = (state: RootState) => state.flips.flips;
export const selectFlipsStatus = (state: RootState) => state.flips.status;
export const selectFlipsError = (state: RootState) => state.flips.error;

export default flipsSlice.reducer;
